"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { useMotionValue } from "motion/react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { NavRailMagnifyContext, type NavRailMagnifyContextValue } from "./NavRailMagnifyContext";
import { ThemeToggleButton } from "./ThemeToggleButton";

/**
 * `<NavRail>` — the Onyx app-shell left rail (the SHELL, `aisquare-ui#128`).
 *
 * A `<nav>` landmark that fills the AppShell rail slot. Structure, top to
 * bottom: the `brand` slot (the AikoMark anchor), a `1px × 28px` hairline
 * divider, the `children` stack (a column of `<RailNavButton>`s), a flex
 * spacer, then the foot cluster (`footer` + an optional `themeToggle`).
 *
 * **The single-listener magnify (the headline interaction).** NavRail owns
 * ONE `mouseAxisPos` `MotionValue` and a SINGLE `onMouseMove` on the nav
 * element. It publishes that value through `NavRailMagnifyContext`; each
 * `RailNavButton` with `magnify` reads it and derives its own
 * distance-to-cursor. So N buttons add zero extra pointer listeners, and the
 * orchestration lives in one place. As the cursor nears a tile it grows to
 * ~1.15 on a spring; ONLY the proximate tile scales — neighbours stay 1.0
 * (the restraint vs the react-bits 5-stop dock). The ramp constants live local
 * to `RailNavButton` (and mirror `MorphingRail`'s), so no shared module.
 *
 * **overflow-visible.** The rail does NOT clip its children, so each
 * `RailNavButton`'s hover-reveal label can fly out to the right of the rail
 * without being cut off.
 *
 * **Tokens.** Authored against the unified rail tokens by NAME with lib-token
 * fallbacks: container `--surface`, right border `--hairline`, width
 * `--rail-w`, accent `--forest` (== `--ac`), ink `--ink`/`--ink-2`. The
 * fallback resolves the rail on-brand inside the lib playground too.
 * TODO(#43): once the steel token set lands in the lib, drop the fallbacks.
 *
 * @example
 *   <NavRail brand={<AikoMark />} themeToggle footer={<AccountCluster />}>
 *     <RailNavButton icon={<HomeIcon />} label="Home" variant="active" />
 *     <RailNavButton icon={<StudioIcon />} label="Studios" />
 *   </NavRail>
 */
export const navRailVariants = cva(
  [
    // overflow-visible so hover labels escape the rail; no horizontal clip.
    "relative flex flex-col items-center overflow-visible",
    "h-full shrink-0",
    "bg-[var(--surface,hsl(var(--background)))]",
    "border-r border-[var(--hairline,hsl(var(--border)))]",
  ].join(" "),
  {
    variants: {
      size: {
        // 72px rail — 14px top / 6px sides / 12px bottom padding, gap 4px.
        default: "w-[var(--rail-w,72px)] px-1.5 pt-3.5 pb-3 gap-1",
        // ≤900px compact rail — 48px wide, tighter padding.
        compact: "w-12 px-1 pt-2.5 pb-2 gap-0.5",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

export interface NavRailProps
  extends
    Omit<React.HTMLAttributes<HTMLElement>, "onMouseMove" | "onMouseLeave">,
    VariantProps<typeof navRailVariants> {
  /** Brand mark anchored at the top (the AikoMark slot). */
  brand?: React.ReactNode;
  /** The destination stack — a column of `<RailNavButton>`s. */
  children?: React.ReactNode;
  /** Bottom-anchored cluster passed through untouched (avatar / help / etc.). */
  footer?: React.ReactNode;
  /** Mount the foot `<ThemeToggleButton variant="rail">`. Default `false`. */
  themeToggle?: boolean;
  /**
   * Enable the dock magnify for opted-in buttons. Default `true`. Disabled
   * automatically under `prefers-reduced-motion`.
   */
  magnify?: boolean;
  /**
   * Accessible name for the `<nav>` landmark. Default `"Primary"`. Always set
   * — multiple landmarks of the same role need distinct names.
   */
  "aria-label"?: string;
  /**
   * Force the compact (48px) layout regardless of viewport. When omitted the
   * rail watches `(max-width: 900px)` and compacts itself, matching the
   * existing `max-[900px]` rail behaviour. Pass `size` to override entirely.
   */
  forceCompact?: boolean;
}

export const NavRail = React.forwardRef<HTMLElement, NavRailProps>(
  (
    {
      brand,
      children,
      footer,
      themeToggle = false,
      magnify = true,
      size,
      forceCompact,
      className,
      "aria-label": ariaLabel = "Primary",
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion() === true;
    const magnifyEnabled = magnify && !reduced;

    // Auto-compact at ≤900px unless the caller pins `size` or `forceCompact`.
    const narrow = useMediaQuery("(max-width: 900px)");
    const resolvedSize: "default" | "compact" =
      size ?? (forceCompact || narrow ? "compact" : "default");

    // The ONE shared cursor tracker. Infinity = "cursor off rail" → every tile
    // clamps to rest. Motion values mutate outside render, so publishing this
    // through context costs nothing per subscriber.
    const mouseAxisPos = useMotionValue<number>(Infinity);

    const handleMouseMove = React.useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (magnifyEnabled) mouseAxisPos.set(e.clientY);
      },
      [magnifyEnabled, mouseAxisPos],
    );
    const handleMouseLeave = React.useCallback(() => {
      if (magnifyEnabled) mouseAxisPos.set(Infinity);
    }, [magnifyEnabled, mouseAxisPos]);

    const ctx = React.useMemo<NavRailMagnifyContextValue>(
      () => ({
        mouseAxisPos: magnifyEnabled ? mouseAxisPos : null,
        enabled: magnifyEnabled,
      }),
      [magnifyEnabled, mouseAxisPos],
    );

    return (
      <NavRailMagnifyContext.Provider value={ctx}>
        <nav
          ref={ref}
          aria-label={ariaLabel}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className={cn(navRailVariants({ size: resolvedSize }), className)}
          {...rest}
        >
          {/* Top anchor — AikoMark brand slot. */}
          {brand ? <div className="flex items-center justify-center shrink-0">{brand}</div> : null}

          {/* 1px × 28px hairline divider under the brand. */}
          {brand ? (
            <span
              aria-hidden="true"
              className="my-2 h-[28px] w-px shrink-0 bg-[var(--hairline,hsl(var(--border)))]"
            />
          ) : null}

          {/* Destination stack. */}
          <div className="flex w-full flex-col items-center gap-1">{children}</div>

          {/* Flex spacer pushes the foot cluster to the bottom. */}
          <div className="flex-1" aria-hidden="true" />

          {/* Foot cluster: passthrough footer + optional theme toggle. */}
          {footer ? (
            <div className="flex w-full flex-col items-center gap-1 shrink-0">{footer}</div>
          ) : null}
          {themeToggle ? (
            <div className="mt-1 flex items-center justify-center shrink-0">
              <ThemeToggleButton
                variant="rail"
                size={resolvedSize === "compact" ? "compact" : "default"}
              />
            </div>
          ) : null}
        </nav>
      </NavRailMagnifyContext.Provider>
    );
  },
);
NavRail.displayName = "NavRail";
